import { useState } from 'react';
import { getBaseApiUrl } from 'core/util/helpers';
import { AUTH_LINK_TOKEN_URL, WARRANTY_BASE } from 'core/constants';
import { buildWarrantyUrl } from 'core/util/warrantyAccess';

interface UseCustomTokenLinkResult {
  isLoading: boolean;
  error: string | null;
  createCustomTokenLink: (hkToken: string, linkTitle: string) => Promise<void>;
}

/**
 * Custom hook that provides functionality for creating a custom token link.
 *
 * This hook manages the loading state and potential errors encountered during
 * the creation of a custom token link. It allows generating a link using
 * a HomeKeep JWT token and a specified link title, and redirects the user to
 * the generated link.
 *
 * @returns {UseCustomTokenLinkResult} An object containing:
 *   - isLoading: A boolean indicating if the process is currently loading.
 *   - error: A string containing the error message if an error occurred, otherwise null.
 *   - createCustomTokenLink: A function that generates and redirects to a custom link
 *     using the provided HomeKeep JWT token and link title.
 */

/**
 * Exchanges a HomeKeep JWT for a one-time link token that avid-warranty-mvp's
 * PrivateRoute consumes (`?link_token=`) to sign the user in on arrival.
 * @param hkToken The homekeep JWT token.
 * @param signal Optional abort signal; also aborts reading the response body.
 * @returns The link token.
 */
export const fetchLinkToken = async (
  hkToken: string,
  signal?: AbortSignal
): Promise<string> => {
  const response = await fetch(getBaseApiUrl() + AUTH_LINK_TOKEN_URL, {
    method: 'GET',
    headers: { Authorization: `Bearer ${hkToken}` },
    signal
  });
  if (!response.ok) {
    throw new Error(`Link token fetch failed: ${response.status}`);
  }
  const data = await response.json();
  return data.link_token;
};

export const useCustomTokenLink = (): UseCustomTokenLinkResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Generates a custom token link for the given `hkToken` and `linkTitle`
   * and redirects the user to the link. If the request fails, an error
   * message is displayed.
   * @param hkToken The homekeep JWT token.
   * @param linkTitle The title of the link.
   */
  const createCustomTokenLink = async (hkToken: string, linkTitle: string) => {
    if (!hkToken) {
      console.error('Token is missing');
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const linkToken = await fetchLinkToken(hkToken);
      window.location.href = buildWarrantyUrl(
        WARRANTY_BASE,
        linkTitle,
        linkToken
      );
      // Uncomment for local testing
      //    window.open(
      //   `http://localhost:6001/${linkTitle}?link_token=${linkToken}`,
      //   '_blank'
      // );
    } catch (err: any) {
      console.error('error:', err);
      setError(err.message || 'Unexpected error');
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    createCustomTokenLink
  };
};
